﻿import { FunctionComponent, ReactNode } from "react";
import { Box, Flex, FlexProps, Heading, Image, Text } from "@chakra-ui/react";

interface ApplicationLayoutProps {
    contentHeader?: ReactNode;
    content?: ReactNode;
    contentContainerProps?: FlexProps;
    footer?: ReactNode;
    buttons?: ReactNode;
    /** Overrides the default container max-width (`calc(48em - 4rem)`) applied on desktop to the header, content, footer, and buttons. */
    maxWidth?: string;
    /** Overrides the container max-width applied on desktop to just the footer and buttons regions. Defaults to `maxWidth`. Useful when `content` needs to be wider (e.g. full-bleed) than the footer/buttons. */
    footerMaxWidth?: string;
}

export const ApplicationLayout: FunctionComponent<ApplicationLayoutProps> = ({
    contentHeader,
    content,
    contentContainerProps,
    footer,
    buttons,
    maxWidth = "calc(48em - 4rem)",
    footerMaxWidth = maxWidth,
}) => {
    return (
        <>
            <Flex direction="column" h={{ base: "100vh", md: "unset" }}>
                <Box
                    boxShadow={contentHeader ? "" : "0 5px 5px rgba(0,0,0,.1)"}
                    bgGradient={"linear(to-r, blue.900, blue.500)"}
                    position={{ base: "unset", md: "sticky" }}
                    top={{ base: "unset", md: 0 }}
                    zIndex={{ base: "unset", md: 1 }}
                    px={"2rem"}
                >
                    <Flex
                        as={"header"}
                        w={{ base: "100%", md: maxWidth }}
                        mx={{ base: "unset", md: "auto" }}
                        height={"100px"}
                        flexShrink={0}
                        justifyContent={"start"}
                        alignItems={"center"}
                        gap={".5rem"}
                    >
                        <Box flexShrink={0}>
                            <Image src={"/acclaim_logo.svg"} boxSize={{ base: 55, md: 65 }} />
                        </Box>
                        <Heading m={0} flexShrink={1}>
                            <Text
                                fontFamily="Helvetica Neue LT Std Black"
                                color={"white"}
                                whiteSpace={"nowrap"}
                                textTransform={"uppercase"}
                                fontSize={{ base: ".75rem", md: "1rem" }}
                                letterSpacing={{ base: ".25rem", md: ".5rem" }}
                                mb={{ base: "0", md: ".2rem" }}
                            >
                                Acclaim Tax
                            </Text>
                            <Text
                                fontFamily="Helvetica Neue LT Std Light"
                                color={"white"}
                                whiteSpace={"nowrap"}
                                textTransform={"uppercase"}
                                fontSize={{ base: "1rem", md: "1.25rem" }}
                                letterSpacing={{ base: ".03rem", md: ".08rem" }}
                            >
                                Work Opportunity Tax Credit
                            </Text>
                        </Heading>
                    </Flex>
                </Box>

                {contentHeader && (
                    <Box
                        py={"1rem"}
                        px={"2rem"}
                        boxShadow={"0 5px 5px rgba(0,0,0,.1)"}
                        position={{ base: "unset", md: "sticky" }}
                        top={{ base: "unset", md: 100 }}
                        zIndex={{ base: "unset", md: 1 }}
                        bg={"white"}
                    >
                        <Box w={{ base: "100%", md: maxWidth }} mx={{ base: "unset", md: "auto" }}>
                            {contentHeader}
                        </Box>
                    </Box>
                )}

                {content && (
                    <Box flexGrow={1} overflowY={"auto"} px={"2rem"}>
                        <Flex
                            as={"main"}
                            w={{ base: "100%", md: maxWidth }}
                            mx={{ base: "unset", md: "auto" }}
                            direction="column"
                            py={"2rem"}
                            gap="2rem"
                            {...contentContainerProps}
                        >
                            {content}
                        </Flex>
                    </Box>
                )}

                {(footer || buttons) && (
                    <Box
                        flexShrink={0}
                        position={{ base: "unset", md: "sticky" }}
                        bottom={{ base: "unset", md: 0 }}
                        zIndex={{ base: "unset", md: 1 }}
                        bg={"white"}
                    >
                        {footer && (
                            <Box px={"2rem"} boxShadow={"0 -5px 5px rgba(0,0,0,.1)"}>
                                <Box w={{ base: "100%", md: footerMaxWidth }} mx={{ base: "unset", md: "auto" }} py={"1rem"}>
                                    {footer}
                                </Box>
                            </Box>
                        )}

                        {buttons && (
                            <Box px={"2rem"} boxShadow={footer ? "unset" : "0 -5px 5px rgba(0,0,0,.1)"}>
                                <Flex
                                    w={{ base: "100%", md: footerMaxWidth }}
                                    mx={{ base: "unset", md: "auto" }}
                                    py={"1rem"}
                                    justifyContent={"space-between"}
                                >
                                    {buttons}
                                </Flex>
                            </Box>
                        )}
                    </Box>
                )}
            </Flex>
        </>
    );
};
